import React, { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import Drawer from '@material-ui/core/Drawer';
import List from '@material-ui/core/List';
import Divider from '@material-ui/core/Divider';
import { useLocation } from 'react-router-dom';
import History from '../../Services/History';
import SidebarHeader from './SidebarHeader';
import SidebarItem from './SidebarItem';
import SidebarGroup from './SidebarGroup';
import { buildMenuConfig } from './config';
import { useActiveRoute } from './useActiveRoute';
import { useSidebarStyles } from './styles';

const Sidebar = ({
	open,
	onCollapse,
	onExpand,
	logoUrl,
	companyName,
	gState,
	language,
	cLanguage,
	powerBIConfig,
	defaultPhotoGallery,
	promoterMode,
	merchandiserMode,
	availableModules,
	modules,
	displayReport,
	competitorModule,
	surveyModule,
	surveyResponseModule,
	csrActivityModule,
	itemDistAnalysisReport,
	itemDistAnalysisReportByAgent,
	stockNearExpiryAnalysisByItem,
	stockNearExpiryAnalysisByDayModule,
	oosInsightModule,
	manageUsersModule,
	onItemNavigate,
	footerItems
}) => {
	const classes = useSidebarStyles();
	const { pathname } = useLocation();

	const sections = useMemo(() => buildMenuConfig({
		language,
		gState,
		powerBIConfig,
		defaultPhotoGallery,
		promoterMode,
		merchandiserMode,
		availableModules,
		modules,
		displayReport,
		competitorModule,
		surveyModule,
		surveyResponseModule,
		csrActivityModule,
		itemDistAnalysisReport,
		itemDistAnalysisReportByAgent,
		stockNearExpiryAnalysisByItem,
		stockNearExpiryAnalysisByDayModule,
		oosInsightModule,
		manageUsersModule
	}), [language, gState, powerBIConfig, defaultPhotoGallery, promoterMode, merchandiserMode, availableModules, modules, displayReport, competitorModule, surveyModule, surveyResponseModule, csrActivityModule, itemDistAnalysisReport, itemDistAnalysisReportByAgent, stockNearExpiryAnalysisByItem, stockNearExpiryAnalysisByDayModule, oosInsightModule, manageUsersModule]);

	const { activeItemKey, activeTrail, activeL1Key, activeL2Key } = useActiveRoute(sections, pathname);

	const [expandedL1, setExpandedL1] = useState(null);
	const [expandedL2, setExpandedL2] = useState(null);

	useEffect(() => {
		if (activeL1Key) setExpandedL1(activeL1Key);
		if (activeL2Key) setExpandedL2(activeL2Key);
	}, [activeL1Key, activeL2Key]);

	const handleToggleGroup = (layer, key) => {
		if (layer === 1) {
			setExpandedL1((prev) => (prev === key ? null : key));
			setExpandedL2(null);
		} else if (layer === 2) {
			setExpandedL2((prev) => (prev === key ? null : key));
		}
	};

	const handleCollapsedGroupClick = (key) => {
		if (onExpand) onExpand();
		setExpandedL1(key);
		setExpandedL2(null);
	};

	const handleItemClick = (item) => {
		if (item.route) {
			History.push(`/main/${String(item.route).toLowerCase()}`);
			if (onItemNavigate) onItemNavigate(item);
		}
	};

	return (
		<Drawer
			variant="permanent"
			className={clsx(classes.drawer, {
				[classes.drawerOpen]: open,
				[classes.drawerClose]: !open
			})}
			classes={{
				paper: clsx({
					[classes.drawerOpen]: open,
					[classes.drawerClose]: !open
				})
			}}>
			<SidebarHeader
				logoUrl={logoUrl}
				companyName={companyName}
				gState={gState}
				onCollapse={onCollapse}
				drawerOpen={open}
			/>
			<List className={classes.listRoot}>
				{sections.map((section) => {
					if (section.children) {
						return (
							<SidebarGroup
								key={section.key}
								section={section}
								layer={1}
								expandedL1={expandedL1}
								expandedL2={expandedL2}
								onToggleGroup={handleToggleGroup}
								activeItemKey={activeItemKey}
								activeTrail={activeTrail}
								drawerOpen={open}
								onItemClick={handleItemClick}
								onCollapsedGroupClick={handleCollapsedGroupClick}
								cLanguage={cLanguage}
								forceL1Indent={!!expandedL1}
							/>
						);
					}
					return (
						<SidebarItem
							key={section.key}
							section={section}
							layer={1}
							active={activeItemKey === section.key}
							drawerOpen={open}
							onClick={() => handleItemClick(section)}
							cLanguage={cLanguage}
							forceL1Indent={!!expandedL1}
						/>
					);
				})}
			</List>
			{footerItems && footerItems.length > 0 && (
				<>
					<Divider className={classes.footerDivider} />
					<List>
						{footerItems.map((item) => (
							<SidebarItem
								key={item.key}
								section={item}
								layer={1}
								active={false}
								drawerOpen={open}
								onClick={item.onClick}
								cLanguage={cLanguage}
								forceL1Indent={!!expandedL1}
							/>
						))}
					</List>
				</>
			)}
		</Drawer>
	);
};

export default Sidebar;
